import { useEffect, useMemo, useState } from 'react'
import type { RepositoryFile } from '@/frontend/features/Fde/types'

export default function useWorkspaceTabs(projectId: string, files: RepositoryFile[], selectedPath?: string) {
  const storageKey = `fde-workspace-tabs:${projectId}`
  const [paths, setPaths] = useState<string[]>([])

  useEffect(() => {
    const available = new Set(files.map((file) => file.path))
    let stored: string[] = []
    try {
      stored = JSON.parse(window.localStorage.getItem(storageKey) || '[]') as string[]
    } catch {
      window.localStorage.removeItem(storageKey)
    }
    const valid = stored.filter((path) => available.has(path))
    const next = selectedPath && available.has(selectedPath) && !valid.includes(selectedPath) ? [...valid, selectedPath] : valid
    setPaths(next)
  }, [storageKey, files, selectedPath])

  useEffect(() => {
    window.localStorage.setItem(storageKey, JSON.stringify(paths))
  }, [storageKey, paths])

  const openFiles = useMemo(() => paths.flatMap((path) => {
    const file = files.find((candidate) => candidate.path === path)
    return file ? [file] : []
  }), [files, paths])

  return {
    openFiles,
    open(path: string) {
      setPaths((current) => {
        const next = current.includes(path) ? current : [...current, path]
        window.localStorage.setItem(storageKey, JSON.stringify(next))
        return next
      })
    },
    close(path: string) {
      setPaths((current) => {
        const next = current.filter((candidate) => candidate !== path)
        window.localStorage.setItem(storageKey, JSON.stringify(next))
        return next
      })
    },
  }
}
